/**
 * overlayPointerGuard — watchdog global contra travamento de interação por overlay.
 *
 * CAUSA RAIZ (categoria, não instância):
 * Radix UI (Dialog/Sheet/AlertDialog/Drawer) aplica, enquanto um modal está aberto:
 *   - `document.body.style.pointer-events: none`
 *   - `aria-hidden="true"` + `inert` nos irmãos do conteúdo no <body>
 * A limpeza desses efeitos acontece no *unmount ordenado* do Radix. Quando um
 * modal é fechado à força (overlayManager pedindo requestClose durante a animação,
 * ou o componente sendo desmontado com `return null` enquanto ainda estava aberto),
 * a limpeza não roda e os atributos VAZAM no <body>. A partir daí, qualquer overlay
 * (inclusive os custom, como o saudação "Boa tarde") aparece na tela mas não recebe
 * cliques — o usuário vê um modal que não fecha no X nem no "Agora não, obrigada".
 *
 * Este guard observa o <body> e, sempre que detecta esses efeitos SEM nenhum modal
 * realmente aberto, restaura a interação. É idempotente e barato (MutationObserver
 * + checagem em rAF), roda em web e WebView nativo.
 */

/** Marcador que overlays custom (não-Radix) podem usar para se declararem abertos. */
export const OVERLAY_OPEN_ATTR = "data-m360-overlay-open";

/**
 * Camadas que REALMENTE bloqueiam a limpeza: apenas modais abertos.
 *
 * IMPORTANTE: popovers/tooltips/dropdowns (`[data-radix-popper-content-wrapper]`)
 * NÃO entram aqui. Eles frequentemente ficam residuais no DOM (a saída do Radix
 * depende de `animationend`, que não dispara em WebView/iframe throttled nem com
 * reduced-motion) e, quando contados como "modal aberto", desligavam o guard
 * PARA SEMPRE — deixando `pointer-events: none` no <body> e travando o app.
 */
const OPEN_MODAL_SELECTOR = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[data-vaul-drawer][data-state="open"]',
  `[${OVERLAY_OPEN_ATTR}="true"]`,
].join(", ");

/** Camadas Radix que já foram fechadas mas continuam montadas (animação travada). */
const CLOSED_LAYER_SELECTOR = [
  '[role="dialog"][data-state="closed"]',
  '[role="alertdialog"][data-state="closed"]',
  '[data-vaul-drawer][data-state="closed"]',
  '[data-radix-popper-content-wrapper] [data-state="closed"]',
  // Backdrop residual: o Overlay do Radix NÃO tem role="dialog", então nunca
  // casava com os seletores acima. Ele fica `fixed inset-0 z-50` com opacity 0
  // e pointer-events auto — invisível e clicável. Foi a causa real do B17
  // ("botão morto" aleatório) e do clique que vazou para a página de baixo.
  '[data-m360-overlay][data-state="closed"]',
].join(", ");

function hasOpenModalLayer(): boolean {
  try {
    return !!document.querySelector(OPEN_MODAL_SELECTOR);
  } catch {
    return true; // na dúvida, não mexe
  }
}

/**
 * Neutraliza camadas fechadas que ficaram montadas: elas são invisíveis mas
 * continuam capturando cliques (fixed inset-0) e Escape. Não removemos do DOM
 * (o React ainda é dono do nó) — apenas tiramos da rota de eventos.
 *
 * IMPORTANTE (grace period): Radix/vaul MONTAM a camada com `data-state="closed"`
 * e só trocam para `open` no frame seguinte. Neutralizar nesse instante mata o
 * overlay que a usuária acabou de abrir — era exatamente o que acontecia no
 * clique REAL do sino (pointerdown → rAF do guard → camada recém-montada
 * neutralizada → vaul desmontava o drawer). O clique sintético não dispara
 * pointerdown, por isso "funcionava". Só neutralizamos camadas fechadas há
 * mais de CLOSED_GRACE_MS.
 */
const CLOSED_GRACE_MS = 800;
const SEEN_CLOSED_ATTR = "data-m360-closed-since";

function neutralizeResidualLayers(): boolean {
  let changed = false;
  const now = Date.now();
  try {
    document.querySelectorAll<HTMLElement>(CLOSED_LAYER_SELECTOR).forEach((el) => {
      const wrapper = el.closest<HTMLElement>("[data-radix-popper-content-wrapper]") ?? el;
      // Backdrop fechado nunca precisa de clique: neutraliza na hora, sem grace.
      const isBackdrop = wrapper.hasAttribute("data-m360-overlay");
      const seenRaw = Number(wrapper.getAttribute(SEEN_CLOSED_ATTR));
      if (!isBackdrop) {
        if (!Number.isFinite(seenRaw) || !seenRaw) {
          wrapper.setAttribute(SEEN_CLOSED_ATTR, String(now));
          return;
        }
        if (now - seenRaw < CLOSED_GRACE_MS) return;
      }
      if (wrapper.style.pointerEvents !== "none") {
        wrapper.style.pointerEvents = "none";
        changed = true;
      }
      if (wrapper.getAttribute("data-m360-neutralized") !== "true") {
        wrapper.setAttribute("data-m360-neutralized", "true");
        changed = true;
      }
    });
    // Camadas que voltaram a abrir perdem o marcador e a neutralização.
    document.querySelectorAll<HTMLElement>(`[${SEEN_CLOSED_ATTR}]`).forEach((wrapper) => {
      if (wrapper.matches(CLOSED_LAYER_SELECTOR)) return;
      if (wrapper.querySelector(CLOSED_LAYER_SELECTOR)) return;
      wrapper.removeAttribute(SEEN_CLOSED_ATTR);
      if (wrapper.getAttribute("data-m360-neutralized") === "true") {
        wrapper.removeAttribute("data-m360-neutralized");
        if (wrapper.style.pointerEvents === "none") {
          wrapper.style.removeProperty("pointer-events");
        }
        changed = true;
      }
    });
  } catch {
    /* noop */
  }
  return changed;
}

/** Remove resíduos de pointer-events/aria-hidden/inert deixados por modais fechados. */
export function releaseStuckOverlayLocks(): boolean {
  if (typeof document === "undefined") return false;

  const body = document.body;
  if (!body) return false;

  // Camadas fechadas-porém-montadas podem ser neutralizadas mesmo com outro
  // modal legitimamente aberto (elas não pertencem a ele).
  let changed = neutralizeResidualLayers();

  if (hasOpenModalLayer()) return changed;

  if (body.style.pointerEvents === "none") {
    body.style.removeProperty("pointer-events");
    changed = true;
  }

  // react-remove-scroll (usado pelo Radix) marca o body e só limpa no unmount
  // ordenado. Sobrando, o scroll da página fica preso depois de fechar o modal.
  if (body.hasAttribute("data-scroll-locked")) {
    body.removeAttribute("data-scroll-locked");
    changed = true;
  }

  Array.from(body.children).forEach((el) => {
    if (el.getAttribute("aria-hidden") === "true") {
      el.removeAttribute("aria-hidden");
      changed = true;
    }
    if (el.hasAttribute("inert")) {
      el.removeAttribute("inert");
      changed = true;
    }
    if ((el as HTMLElement).style?.pointerEvents === "none") {
      (el as HTMLElement).style.removeProperty("pointer-events");
      changed = true;
    }
  });

  if (changed && import.meta.env.DEV) {
    console.warn("[overlayPointerGuard] resíduo de modal removido do <body>");
  }
  return changed;
}


let installed = false;

export function installOverlayPointerGuard(): void {
  if (installed || typeof document === "undefined") return;
  installed = true;

  let frame = 0;
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      releaseStuckOverlayLocks();
    });
  };

  // 1) Reage a qualquer mutação de style/aria-hidden/inert no body e filhos diretos.
  const observer = new MutationObserver(schedule);
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ["style", "aria-hidden", "inert"],
    childList: true,
    subtree: false,
  });

  // 2) Rede de segurança: se o usuário tenta interagir e o body está travado,
  //    libera na hora (capture, antes de qualquer handler).
  const onUserIntent = () => schedule();
  document.addEventListener("pointerdown", onUserIntent, true);
  document.addEventListener("keydown", onUserIntent, true);
  window.addEventListener("focus", onUserIntent);

  // 3) Varredura periódica leve — cobre casos sem interação (ex: navegação programática).
  window.setInterval(() => releaseStuckOverlayLocks(), 1500);
}
